import React, { useState, useEffect } from 'react';
import { FileText, Check, Clock, PenTool, Search, Filter, X } from 'lucide-react';
import StatsCard from '../utils/StatsCard';
import QuickActions from './QuickAction';
import RecentForms from './RecentActivities';
import { getAllEmploymentApplications } from '../../services/Queries/SupabaseQueries';
import { useAuths } from '../../context/AuthProviders';
import { getUserProfile } from '../../services/Queries/Auths';

const Main = () => {
  const { user, loading: authLoading, logout } = useAuths();

  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);

  const [allApplications, setAllApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dateRange: 'all',
  });
  const [stats, setStats] = useState({
    totalDrafts: 0,
    pendingDrafts: 0,
    finalizedDrafts: 0,
    reviewedDrafts: 0,
  });

  useEffect(() => {
    const run = async () => {
      if (authLoading) return;

      if (!user?.id) {
        setProfileLoading(false);
        return;
      }

      try {
        setProfileLoading(true);
        setProfileError(null);

        const profile = await getUserProfile(user.id);

        if (!profile) {
          // Clean up storage + force login redirect
          try {
            localStorage.clear();
            sessionStorage.clear();
          } catch {}
          try {
            if (logout) await logout();
          } catch {}
          window.location.href = '/login';
          return;
        }

        setProfileData(profile);
      } catch (err) {
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch {}
        try {
          if (logout) await logout();
        } catch {}
        window.location.href = '/login';
      } finally {
        setProfileLoading(false);
      }
    };

    run();
  }, [user?.id, authLoading, logout]);

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [allApplications]);

  useEffect(() => {
    applyFilters();
  }, [filters, allApplications]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const data = await getAllEmploymentApplications();
      setAllApplications(data || []);
      setFilteredApplications(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const total = allApplications.length;
    const pending = allApplications.filter((a) => a.status === 'draft').length;
    const reviewed = allApplications.filter((a) => a.status === 'hr_reviewed').length;
    const finalized = allApplications.filter((a) => a.status === 'finalized').length;

    setStats({
      totalDrafts: total,
      pendingDrafts: pending,
      reviewedDrafts: reviewed,
      finalizedDrafts: finalized,
    });
  };

  const applyFilters = () => {
    let filtered = [...allApplications];

    if (filters.status !== 'all') {
      filtered = filtered.filter((app) => app.status === filters.status);
    }

    if (filters.search.trim()) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter((app) => {
        const fd = app.form_data || {};
        return (
          fd.employeeName?.toLowerCase().includes(s) ||
          fd.firstName?.toLowerCase().includes(s) ||
          fd.lastName?.toLowerCase().includes(s) ||
          fd.email?.toLowerCase().includes(s)
        );
      });
    }

    if (filters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter((app) => {
        const appDate = new Date(app.created_at);
        switch (filters.dateRange) {
          case 'today':
            return appDate >= today;
          case 'week': {
            const w = new Date(today);
            w.setDate(w.getDate() - 7);
            return appDate >= w;
          }
          case 'month': {
            const m = new Date(today);
            m.setMonth(m.getMonth() - 1);
            return appDate >= m;
          }
          default:
            return true;
        }
      });
    }

    setFilteredApplications(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: 'all', search: '', dateRange: 'all' });
  };

  const statItems = [
    { label: 'Total Drafts', value: stats.totalDrafts, icon: FileText, color: 'text-blue-600' },
    { label: 'Pending', value: stats.pendingDrafts, icon: Clock, color: 'text-yellow-600' },
    { label: 'HR Reviewed', value: stats.reviewedDrafts, icon: PenTool, color: 'text-purple-600' },
    { label: 'Finalized', value: stats.finalizedDrafts, icon: Check, color: 'text-green-600' },
  ];

  if (authLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (!user || !profileData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Redirecting to login…</div>
      </div>
    );
  }

  if (profileData.user_type !== 'hr' && profileData.user_type !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="bg-red-500 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <X className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            This area is restricted to HR and Admin users.
          </p>
          <button
            onClick={logout}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        Error loading dashboard: {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold text-slate-800">Dashboard Overview</h2>
          <p className="text-slate-500 mt-1">
            Welcome back, {profileData?.first_name || profileData?.email}
          </p>
        </div>
        <div className="bg-purple-100 px-4 py-2 rounded-lg">
          <span className="text-sm font-medium text-purple-800">
            Role: {profileData?.user_type === 'admin' ? 'Administrator' : 'HR Manager'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {statItems.map((stat, idx) => (
          <StatsCard
            key={idx}
            label={stat.label}
            value={stat.value}
            Icon={stat.icon}
            color={stat.color}
          />
        ))}
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Filter size={18} /> Filter Applications
          </h3>
          <span className="text-sm text-gray-600">
            Showing {filteredApplications.length} of {allApplications.length}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="hr_reviewed">HR Reviewed</option>
              <option value="finalized">Finalized</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>

        {(filters.status !== 'all' || filters.search || filters.dateRange !== 'all') && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
            >
              <X size={14} /> Clear All Filters
            </button>
          </div>
        )}
      </div>

      <QuickActions />
      <RecentForms applications={filteredApplications} />
    </div>
  );
};

export default Main;